import { Box, Group, ScrollArea, Text } from "@mantine/core";
import { createFileRoute } from "@tanstack/react-router";
import { Duration } from "luxon";
import { useQuery } from "@tanstack/react-query";
import { useRecordingContext } from "../-RecordingContext";
import type { Transcription } from "@/models/Transcription";
import { TranscriptionStatusMap } from "@/models/Recording";
import LoadingPane from "@/components/LoadingPane";
import { useApi } from "@/api";
import { getRecordingTranscriptionQuery } from "@/api/recordings/getRecordingTranscription";

export const Route = createFileRoute(
  "/prospects/$prospectId/recordings/$recordingId/transcript"
)({
  component: RouteComponent,
  loader: () => {
    return {
      crumb: "Transcript"
    };
  }
});

function RouteComponent() {
  const { recordingId } = Route.useParams();
  const { secureAxios } = useApi();
  const { handleSeekToTime: onSeekToTime } = useRecordingContext();

  const { data: transcriptionData } = useQuery({
    ...getRecordingTranscriptionQuery(secureAxios, recordingId),
    // Only refetch on mount/focus if transcription isn't finalized yet
    refetchOnMount: (query) =>
      query.state.data?.transcriptionStatus !==
      TranscriptionStatusMap.Succeeded,
    refetchOnWindowFocus: false
  });

  if (!transcriptionData) {
    return <LoadingPane />;
  }

  const handlePhraseClick = (offsetMs: number) => {
    const timeInSeconds = offsetMs / 1000;
    onSeekToTime?.(timeInSeconds);
  };

  const transcript = transcriptionData.transcriptionJson
    ? (JSON.parse(transcriptionData.transcriptionJson) as Transcription)
    : undefined;

  return transcript ? (
    <ScrollArea h="100%" type="always">
      {transcript.recognizedPhrases.map((phrase) => {
        const bestResult = phrase.nBest[0];
        const time = Duration.fromMillis(phrase.offsetMilliseconds);
        const timeLabel = time.toFormat("mm:ss");
        const speakerLabel = `Speaker ${phrase.speaker}`;
        return (
          <Box
            key={phrase.offsetMilliseconds}
            style={{ cursor: "pointer" }}
            onClick={() => handlePhraseClick(phrase.offsetMilliseconds)}
          >
            <Group justify="space-between" gap="s" align="center">
              <Text c="optimaBlue.9" fz="sm">
                {speakerLabel}
              </Text>
              <Text c="optimaBlue.9" fz="sm">
                {timeLabel}
              </Text>
            </Group>
            <Text mb="sm">{bestResult.display}</Text>
          </Box>
        );
      })}
    </ScrollArea>
  ) : (
    <div>No transcript available.</div>
  );
}
